import React, { useState, useEffect } from "react";
import { Button, Box } from "@mui/material";
import dayjs from "dayjs";
import { Add } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AxiosInstance from "../../services/networkServices";
import { validateFileExtension } from "../../utils";
import ConfirmDeleteModal from "../ManageBadge/ConfirmDeleteModal";
import useDebounce from "../ManageUsers/useDebounce";
import PageLayout from "../../components/PageLayout";
import { uploadImage } from "../../http/uploads";
import { showToast } from "../../store/slices/common";
import { useDispatch } from "react-redux";
import { usePermissions } from "../../hooks/usePermissions";
import { listAudios, updateAudio } from "../../http/audio";
import ContestTable from "./ContestTable";
import ContestFormModal from "./ContestFormModal";
import ContestDetailsModal from "./ContestDetailsModal";

const limit = 15;

const DEFAULT_REGIONAL_SLOTS = [
  { prizeCount: "1", entriesRequired: "10" },
  { prizeCount: "2", entriesRequired: "30" },
  { prizeCount: "3", entriesRequired: "60" },
  { prizeCount: "4", entriesRequired: "100" },
  { prizeCount: "5", entriesRequired: "200" },
];

const isSpecialContest = (contest) => {
  if (!contest) return false;
  if (contest.seriesId) return true;
  return /^(girl group:|boy group:)/i.test(contest.title || "");
};

/** New admin contests, and platform contests whose bracket has not started. */
const regionalPrizesEditable = (contest) => {
  if (isSpecialContest(contest)) return false;
  if (!contest) return true;
  if (!contest.isPlatformContest && !contest.regionalResults?.enabled) return false;
  if (contest.regionalResults?.enabled) return true;
  if (contest.currentRound > 0) return false;
  return dayjs().isBefore(dayjs(contest.votingStartDate));
};

const buildRegionalPayload = (amount, slots, regionsText) => ({
  enabled: true,
  prizeAmount: Math.max(1, parseInt(amount, 10) || 10),
  prizeSlots: slots.map((slot) => ({
    prizeCount: parseInt(slot.prizeCount, 10),
    entriesRequired: parseInt(slot.entriesRequired, 10),
  })),
  prizeRegions: String(regionsText || "")
    .split(",")
    .map((region) => region.trim())
    .filter((region) => region.length >= 2),
});

const regionalSlotsAreValid = (slots) => {
  let previousEntries = 0;
  let previousPrizes = 0;
  return slots.every((slot) => {
    const prizeCount = parseInt(slot.prizeCount, 10);
    const entriesRequired = parseInt(slot.entriesRequired, 10);
    const ok =
      prizeCount >= 1 &&
      entriesRequired >= 1 &&
      entriesRequired > previousEntries &&
      prizeCount > previousPrizes;
    previousEntries = entriesRequired;
    previousPrizes = prizeCount;
    return ok;
  });
};
const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  dialogContent: {
    maxWidth: "100%",
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  input: {
    marginBottom: "0 !important",
    "& label.Mui-focused": {
      color: "#142C5C",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#142C5C",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& fieldset": {
        borderColor: "#142C5C",
      },
      "&:hover fieldset": {
        borderColor: "#142C5C",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#142C5C",
      },
    },
  },
  button: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    borderColor: "#142C5C !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  cancelButton: {
    backgroundColor: "#d32f2f",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#b71c1c",
    },
    marginRight: theme.spacing(2),
  },
  saveButton: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1b5e20",
    },
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: "10px",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: "calc(100vh - 205px)",
    maxHeight: "calc(100vh - 205px)",
    boxShadow: "none !important",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  tableCell: {
    fontSize: {
      xs: "14px !important",
      sm: "15px !important",
      md: "15px !important",
      lg: "16px !important",
      xl: "16px !important",
    },
    color: "#000 !important",
    fontWeight: "600 !important",
    padding: {
      xs: "10px 5px !important",
      sm: "15px 10px !important",
      md: "15px 10px !important",
      lg: "20px 15px !important",
      xl: "20px 15px !important",
    },
    backgroundColor: "#e0e0e0",
  },
  actionsCell: {
    textAlign: "right",
  },
  tableBody: {
    fontSize: {
      xs: "12px !important",
      sm: "13px !important",
      md: "14px !important",
      lg: "15px !important",
      xl: "15px !important",
    },
    color: "#333 !important",
    padding: {
      xs: "10px 5px !important",
      sm: "10px !important",
      md: "10px !important",
      lg: "10px 15px !important",
      xl: "10px 15px !important",
    },
  },
  table: {
    border: "1px solid rgba(224, 224, 224, 1)",
    minWidth: "100%",
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
}));

export default function ManageContests(props) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [contests, setContests] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [submissionStartDate, setSubmissionStartDate] = useState(null);
  const [submissionEndDate, setSubmissionEndDate] = useState(null);
  const [votingStartDate, setVotingStartDate] = useState(null);
  const [votingEndDate, setVotingEndDate] = useState(null);
  const [textData, setTextData] = useState({
    title: "",
    prize: "",
    details: "",
  });
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(0);
  const [totalContests, setTotalContests] = useState(0);
  const [search, setSearch] = useState("");
  const [fileData, setFileData] = useState(null);
  const [backgroundFileData, setBackgroundFileData] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const [showListDate, setShowListDate] = useState(null);
  const [votingDurationHours, setVotingDurationHours] = useState(168); // Default 168 hours (7 days)
  const debouncedSearch = useDebounce(search);
  const dispatch = useDispatch();
  const { isAdmin } = usePermissions();
  
  // Audio assignment state
  const [allAudios, setAllAudios] = useState([]);
  const [selectedAudioIds, setSelectedAudioIds] = useState([]);
  const [originalAudioIds, setOriginalAudioIds] = useState([]);
  const [regionalPrizeAmount, setRegionalPrizeAmount] = useState("10");
  const [regionalSlots, setRegionalSlots] = useState(DEFAULT_REGIONAL_SLOTS);
  const [regionalRegionsText, setRegionalRegionsText] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    setTextData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setTextData({
      title: "",
      prize: "",
      details: "",
    });
    setVotingStartDate(null);
    setVotingEndDate(null);
    setSubmissionStartDate(null);
    setShowListDate(null);
    setSubmissionEndDate(null);
    setSelectedItem(null);
    setFileData(null);
    setBackgroundFileData(null);
    setVotingDurationHours(168); // Reset to default (168 hours = 7 days)
    setSelectedAudioIds([]);
    setOriginalAudioIds([]);
    setRegionalPrizeAmount("10");
    setRegionalSlots(DEFAULT_REGIONAL_SLOTS);
    setRegionalRegionsText("");
  };

  const updateRegionalSlot = (index, field, value) => {
    setRegionalSlots((current) =>
      current.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot
      )
    );
  };

  const handleFileUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    // Validate file type by MIME type and file extension
    const validFormats = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
    const isValidMimeType = file.type && validFormats.includes(file.type);
    const isValidExtension = validateFileExtension(file.name, "");
    
    if (!isValidMimeType && !isValidExtension) {
      dispatch(
        showToast({
          color: "error",
          msg: "Invalid file format. Please upload a JPG, JPEG, or PNG image.",
        })
      );
      onError(new Error("Invalid file format"));
      return;
    }

    // Validate file size (5MB max)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      dispatch(
        showToast({
          color: "error",
          msg: "File size exceeds 5MB limit. Please upload a smaller file.",
        })
      );
      onError(new Error("File size too large"));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "asset");
      setLoading(true);
      const res = await uploadImage(formData);
      setFileData(res.data?.data);
      dispatch(
        showToast({
          color: "success",
          msg: "Logo uploaded successfully.",
        })
      );
      onSuccess(res.data?.data);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg: error.response?.data?.message || "Failed to upload logo. Please try again.",
        })
      );
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackgroundFileUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    // Validate file type by MIME type and file extension
    const validFormats = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
    const isValidMimeType = file.type && validFormats.includes(file.type);
    const isValidExtension = validateFileExtension(file.name, "");
    
    if (!isValidMimeType && !isValidExtension) {
      dispatch(
        showToast({
          color: "error",
          msg: "Invalid file format. Please upload a JPG, JPEG, or PNG image.",
        })
      );
      onError(new Error("Invalid file format"));
      return;
    }

    // Validate file size (5MB max)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      dispatch(
        showToast({
          color: "error",
          msg: "File size exceeds 5MB limit. Please upload a smaller file.",
        })
      );
      onError(new Error("File size too large"));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "background");
      setLoading(true);
      const res = await uploadImage(formData);
      setBackgroundFileData(res.data?.data);
      dispatch(
        showToast({
          color: "success",
          msg: "Background image uploaded successfully.",
        })
      );
      onSuccess(res.data?.data);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg: error.response?.data?.message || "Failed to upload background image. Please try again.",
        })
      );
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContests();
  }, [page, debouncedSearch]);

  const fetchContests = async () => {
    let payload = {
      page: page + 1,
      limit: limit,
    };
    if (debouncedSearch) payload = { ...payload, search: debouncedSearch };
    try {
      setLoading(true);
      const response = await AxiosInstance.get(
        `/contest?page=${page + 1}&limit=${limit}${
          debouncedSearch ? `&search=${debouncedSearch}` : ""
        }`
      );
      if (response.data.status) {
        setContests(response.data.data.list);
        setTotalContests(response.data.data.resultCount);
      } else {
        console.error("Failed to fetch categories:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkDisabled = () => {
    if (
      textData?.title &&
      textData?.prize &&
      textData?.details &&
      submissionStartDate &&
      votingStartDate
    ) {
      return false;
    }
    return false;
  };

  const handleAddContest = async () => {
    // Validate required fields. `undefined < 3` is false, so an untouched
    // field has to be checked explicitly or an empty form sails through to
    // the API and fails there instead of inline.
    if ((textData?.title?.trim().length ?? 0) < 3) {
      dispatch(showToast({ color: "error", msg: "Title is too short." }));
      return;
    }
    // Prize is optional, but if provided, must be at least 3 characters
    if (textData?.prize && textData?.prize.length < 3) {
      dispatch(showToast({ color: "error", msg: "Prize is too short." }));
      return;
    }
    if ((textData?.details?.trim().length ?? 0) < 3) {
      dispatch(
        showToast({
          color: "error",
          msg: "Detail is too short.",
        })
      );
      return;
    }
    if (!votingStartDate) {
      dispatch(showToast({ color: "error", msg: "Please select voting start date." }));
      return;
    }
    if (!votingDurationHours || votingDurationHours < 1) {
      dispatch(showToast({ color: "error", msg: "Voting duration must be at least 1 hour." }));
      return;
    }
    if (!regionalSlotsAreValid(regionalSlots)) {
      dispatch(
        showToast({
          color: "error",
          msg: "Each prize step needs more prizes and more entries than the one before it.",
        })
      );
      return;
    }

    // Calculate dates automatically
    const now = dayjs(); // Current time
    const votingStart = dayjs(votingStartDate);
    const votingEnd = votingStart.add(votingDurationHours, 'hour');
    const submissionStart = now; // Start immediately
    const submissionEnd = votingStart; // End when voting begins
    const showInListDate = now; // Show immediately

    const payload = {
      title: textData?.title,
      details: textData?.details,
      startDate: submissionStart.toISOString(),
      endDate: submissionEnd.toISOString(),
      votingStartDate: votingStart.toISOString(),
      votingEndDate: votingEnd.toISOString(),
      showInCategoriesListFromDate: showInListDate.toISOString(),
      logoId: fileData?._id,
      backgroundImageId: backgroundFileData?._id ?? null,
    };
    
    // Send prize value or null if empty
    payload.prize = (textData?.prize && textData?.prize.trim()) ? textData.prize : null;
    payload.regionalResults = buildRegionalPayload(
      regionalPrizeAmount,
      regionalSlots,
      regionalRegionsText
    );

    try {
      setLoading(true);
      const response = await AxiosInstance.post("/contest", payload);
      if (response.status === 200) {
        fetchContests();
        setOpenDialog(false);
        resetForm();
        dispatch(
          showToast({
            color: "success",
            msg: "Contest added successfully.",
          })
        );
      }
    } catch (error) {
      console.error("Error adding challenge:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditContest = async () => {
    if ((textData?.title?.trim().length ?? 0) < 3) {
      dispatch(showToast({ color: "error", msg: "Title is too short." }));
      return;
    }
    if ((textData?.details?.trim().length ?? 0) < 3) {
      dispatch(showToast({ color: "error", msg: "Detail is too short." }));
      return;
    }
    const payload = {
      id: selectedItem?._id,
      title: textData?.title,
      details: textData?.details,
      logoId: fileData?._id,
      // Explicit null clears a removed background (undefined is omitted from JSON)
      backgroundImageId: backgroundFileData?._id ?? null,
    };
    
    // Only include prize if it has a value, otherwise send null to clear it
    if (textData?.prize && textData?.prize.trim()) {
      payload.prize = textData.prize;
    } else {
      payload.prize = null; // Explicitly set to null to clear the prize
    }

    if (regionalPrizesEditable(selectedItem)) {
      if (!regionalSlotsAreValid(regionalSlots)) {
        dispatch(
          showToast({
            color: "error",
            msg: "Each prize step needs more prizes and more entries than the one before it.",
          })
        );
        return;
      }
      payload.regionalResults = buildRegionalPayload(
        regionalPrizeAmount,
        regionalSlots,
        regionalRegionsText
      );
    }

    try {
      setLoading(true);

      // Only send voting start date if it changed (submission end will be auto-calculated)
      if (
        votingStartDate &&
        new Date(votingStartDate).toISOString() !==
        new Date(selectedItem?.votingStartDate).toISOString()
      ) {
        payload.votingStartDate = new Date(votingStartDate).toISOString();
        // Automatically set submission end date to 15 minutes before voting start
        payload.endDate = new Date(dayjs(votingStartDate).subtract(15, 'minute')).toISOString();
      }

      // Only send voting end date if it changed
      if (
        votingEndDate &&
        new Date(votingEndDate).toISOString() !==
        new Date(selectedItem?.votingEndDate).toISOString()
      ) {
        payload.votingEndDate = new Date(votingEndDate).toISOString();
      }

      // Always send show in category list date and submission start date together
      // This ensures the backend validates them as a pair
      // Submission start date is always 1 minute after show list date
      if (showListDate) {
        const showListDateTime = dayjs(showListDate);
        const submissionStartDateTime = showListDateTime.add(1, 'minute');
        
        payload.showInCategoriesListFromDate = showListDateTime.toISOString();
        payload.startDate = submissionStartDateTime.toISOString();
      }

      // Add validation for prize (only if prize is provided)
      if (textData?.prize && textData?.prize.length < 3) {
        dispatch(showToast({ color: "error", msg: "Prize is too short." }));
        return;
      }

      const response = await AxiosInstance.put("/contest", payload);
      if (response.status === 200) {
        // Handle audio assignment changes
        const addedAudioIds = selectedAudioIds.filter(id => !originalAudioIds.includes(id));
        const removedAudioIds = originalAudioIds.filter(id => !selectedAudioIds.includes(id));
        
        // Update audios that were added to this contest
        for (const audioId of addedAudioIds) {
          const audio = allAudios.find(a => a._id === audioId);
          if (audio) {
            const existingContestIds = audio.contests?.map(c => c._id) || [];
            const formData = new FormData();
            formData.append('id', audioId);
            formData.append('name', audio.name);
            // Add existing contests plus this new one
            [...existingContestIds, selectedItem._id].forEach(cId => {
              formData.append('contestIds[]', cId);
            });
            // Keep existing category IDs
            audio.categories?.forEach(cat => {
              formData.append('categoryIds[]', cat._id);
              formData.append(`categoryStatus[${cat._id}]`, cat.status || 'ACTIVE');
            });
            try {
              await updateAudio(formData);
            } catch (err) {
              console.error('Error adding audio to contest:', err);
            }
          }
        }
        
        // Update audios that were removed from this contest
        for (const audioId of removedAudioIds) {
          const audio = allAudios.find(a => a._id === audioId);
          if (audio) {
            const updatedContestIds = (audio.contests?.map(c => c._id) || [])
              .filter(cId => cId !== selectedItem._id);
            const formData = new FormData();
            formData.append('id', audioId);
            formData.append('name', audio.name);
            // Add remaining contests (excluding this one)
            updatedContestIds.forEach(cId => {
              formData.append('contestIds[]', cId);
            });
            // Keep existing category IDs
            audio.categories?.forEach(cat => {
              formData.append('categoryIds[]', cat._id);
              formData.append(`categoryStatus[${cat._id}]`, cat.status || 'ACTIVE');
            });
            try {
              await updateAudio(formData);
            } catch (err) {
              console.error('Error removing audio from contest:', err);
            }
          }
        }
        
        fetchContests();
        setOpenDialog(false);
        resetForm();
        dispatch(
          showToast({
            color: "success",
            msg: "Contest updated successfully.",
          })
        );
      }
    } catch (error) {
      console.error("Error editing challenge:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.error?.message || "Failed to update contest.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewChallenge = (index) => {
    setSelectedItem(contests[index]);
    setOpenViewDialog(true);
  };

  // Fetch all audios for assignment
  const fetchAllAudios = async () => {
    try {
      const payload = { page: 1, limit: 500 };
      const res = await listAudios(payload);
      setAllAudios(res?.data?.list || []);
      return res?.data?.list || [];
    } catch (err) {
      console.error('Error fetching audios:', err);
      return [];
    }
  };

  const handleEditChallenge = async (index) => {
    const contest = contests[index];
    const txtData = {
      title: contest?.title || '',
      prize: contest?.prize || '',
      details: contest?.details || '',
    };
    setTextData(txtData);
    setVotingStartDate(dayjs(contest?.votingStartDate));
    setVotingEndDate(dayjs(contest?.votingEndDate));
    setSubmissionStartDate(dayjs(contest?.startDate));
    // Set submission end date to 15 minutes before voting start
    const votingStart = dayjs(contest?.votingStartDate);
    setSubmissionEndDate(votingStart.subtract(15, 'minute'));
    setShowListDate(dayjs(contest?.showInCategoriesListFromDate));
    setSelectedItem(contest);
    const regional = contest?.regionalResults;
    if (regional?.enabled) {
      setRegionalPrizeAmount(String(regional.prizeAmount || 10));
      setRegionalSlots(
        (regional.prizeSlots || []).length
          ? regional.prizeSlots.map((slot) => ({
              prizeCount: String(slot.prizeCount),
              entriesRequired: String(slot.entriesRequired),
            }))
          : DEFAULT_REGIONAL_SLOTS
      );
      setRegionalRegionsText(
        (regional.prizeRegions || [])
          .map((region) => region.label || region)
          .join(", ")
      );
    } else {
      setRegionalPrizeAmount("10");
      setRegionalSlots(DEFAULT_REGIONAL_SLOTS);
      setRegionalRegionsText("");
    }
    
    // Reset file data to avoid conflict with previous selections
    setFileData(null);
    setBackgroundFileData(null);

    // Set the logo and background image from the selected contest if they exist
    if (contest?.backgroundImage) {
      setBackgroundFileData({
        url: contest?.backgroundImage,
        _id: contest?.backgroundImageId
      });
    }
    
    // Fetch audios and pre-select ones assigned to this contest
    const audios = await fetchAllAudios();
    const assignedAudioIds = audios
      .filter(audio => audio.contests?.some(c => c._id === contest._id))
      .map(audio => audio._id);
    setSelectedAudioIds(assignedAudioIds);
    setOriginalAudioIds(assignedAudioIds);
    
    setOpenDialog(true);
  };

  // Handler for voting start date change - automatically update submission end date
  const handleVotingStartDateChange = (date) => {
    setVotingStartDate(date);
    if (!date) return;

    // Submission must end before voting begins (15 minute buffer)
    const newSubmissionEnd = dayjs(date).subtract(15, 'minute');
    setSubmissionEndDate(newSubmissionEnd);

    // If moving the voting time earlier pushes the submission start on/after the
    // new submission end, shift the whole submission window (and the show-list
    // date that drives it) earlier so the submission period stays entirely
    // before the voting period. Otherwise the backend rejects the update
    // because the resulting dates are invalid / in the past.
    const currentStart = submissionStartDate ? dayjs(submissionStartDate) : null;
    if (!currentStart || !currentStart.isBefore(newSubmissionEnd)) {
      // Preserve the existing submission window length when we can, otherwise
      // fall back to a 1 hour submission window.
      let windowMinutes = 60;
      if (currentStart && submissionEndDate) {
        const prevLength = dayjs(submissionEndDate).diff(currentStart, 'minute');
        if (prevLength > 0) {
          windowMinutes = prevLength;
        }
      }

      const newSubmissionStart = newSubmissionEnd.subtract(windowMinutes, 'minute');
      setSubmissionStartDate(newSubmissionStart);
      // Submission start is always 1 minute after the show-list date.
      setShowListDate(newSubmissionStart.subtract(1, 'minute'));
    }
  };

  // Handler for show in category list date change - also updates submission start date
  const handleShowListDateChange = (date) => {
    setShowListDate(date);
    // Set submission start date to 1 minute after show list date
    if (date) {
      setSubmissionStartDate(dayjs(date).add(1, 'minute'));
    }
  };

  const handleDeleteChallenge = (idx) => {
    if (!isAdmin) {
      dispatch(
        showToast({
          color: "error",
          msg: "You are not authorized to delete contests.",
        })
      );
      return;
    }
    setOpenDeleteModal(true);
    setDeleteIndex(idx);
  };

  const confirmDeleteChallenge = async () => {
    try {
      const deletedChallenge = contests[deleteIndex];

      if (!deletedChallenge || !deletedChallenge._id) {
        throw new Error("Contest ID is missing or invalid.");
      }

      const response = await AxiosInstance.delete(
        `/contest/${deletedChallenge._id}`
      );

      if (response.status === 200) {
        await fetchContests();
        setOpenDeleteModal(false);
        dispatch(
          showToast({
            color: "error",
            msg: "Contest deleted successfully.",
          })
        );
      } else {
        dispatch(
          showToast({ color: "error", msg: "Failed to delete challenge" })
        );
      }
    } catch (error) {
      toast.error("Failed to delete challenge.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: { backgroundColor: "#f44336" },
      });
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const closeForm = () => {
    resetForm();
    setOpenDialog(false);
  };

  const removeLogo = () => {
    setFileData(null);
    if (selectedItem) {
      setSelectedItem({ ...selectedItem, logo: null });
    }
  };

  const removeBackground = () => {
    setBackgroundFileData(null);
    if (selectedItem) {
      setSelectedItem({
        ...selectedItem,
        backgroundImage: null,
        backgroundImageId: null,
      });
    }
  };

  const deleteFromForm = () => {
    const index = contests.findIndex((contest) => contest._id === selectedItem._id);
    if (index !== -1) {
      setDeleteIndex(index);
      setOpenDeleteModal(true);
      setOpenDialog(false);
    }
  };

  const closeDetails = () => {
    setSelectedItem(null);
    setOpenViewDialog(false);
  };

  return (
    <PageLayout
      title="Contests"
      loading={loading}
      actions={
        <Button
          sx={{
            padding: {
              xs: "6px",
              sm: "6px 16px",
              md: "6px 16px",
              lg: "6px 16px",
              xl: "6px 16px",
            },
            backgroundColor: "#142C5C",
            color: "#fff",
            borderColor: "#0f2247",
            "&:hover": {
              backgroundColor: "#0f2247 !important",
            },
            borderRadius: "6px",
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "13px",
              lg: "14px",
              xl: "14px",
            },
            height: {
              xs: "35px",
              sm: "40px",
              md: "40px",
              lg: "40px",
              xl: "40px",
            },
          }}
          variant="contained"
          onClick={() => setOpenDialog(true)}
        >
          <Add /> Add Contest
        </Button>
      }
      search={
        <input
          placeholder="Search"
          type="text"
          onChange={handleSearchChange}
          className="search-field-ui"
        />
      }
    >
      <Box className={classes.root}>
        <ContestTable
          classes={classes}
          contests={contests}
          isAdmin={isAdmin}
          limit={limit}
          totalContests={totalContests}
          page={page}
          onPageChange={handlePageChange}
          onView={handleViewChallenge}
          onEdit={handleEditChallenge}
          onDelete={handleDeleteChallenge}
        />
        <ContestFormModal
          classes={classes}
          open={openDialog}
          selectedItem={selectedItem}
          isAdmin={isAdmin}
          textData={textData}
          onTextChange={onChange}
          fileData={fileData}
          backgroundFileData={backgroundFileData}
          onFileUpload={handleFileUpload}
          onBackgroundFileUpload={handleBackgroundFileUpload}
          onRemoveLogo={removeLogo}
          onRemoveBackground={removeBackground}
          submissionStartDate={submissionStartDate}
          submissionEndDate={submissionEndDate}
          votingStartDate={votingStartDate}
          votingEndDate={votingEndDate}
          showListDate={showListDate}
          votingDurationHours={votingDurationHours}
          onVotingStartDateChange={
            selectedItem ? handleVotingStartDateChange : setVotingStartDate
          }
          onVotingEndDateChange={setVotingEndDate}
          onShowListDateChange={handleShowListDateChange}
          onVotingDurationChange={(event) => {
            const value = parseInt(event.target.value) || 1;
            setVotingDurationHours(value > 0 ? value : 1);
          }}
          allAudios={allAudios}
          selectedAudioIds={selectedAudioIds}
          onSelectedAudioIdsChange={setSelectedAudioIds}
          onDelete={deleteFromForm}
          onClose={closeForm}
          onSubmit={selectedItem ? handleEditContest : handleAddContest}
          showRegionalPrizes={regionalPrizesEditable(selectedItem)}
          regionalPrizeAmount={regionalPrizeAmount}
          onRegionalPrizeAmountChange={setRegionalPrizeAmount}
          regionalSlots={regionalSlots}
          onRegionalSlotChange={updateRegionalSlot}
          onAddRegionalSlot={() =>
            setRegionalSlots((current) => [
              ...current,
              { prizeCount: "", entriesRequired: "" },
            ])
          }
          onRemoveRegionalSlot={(index) =>
            setRegionalSlots((current) => current.filter((_, slotIndex) => slotIndex !== index))
          }
          regionalRegionsText={regionalRegionsText}
          onRegionalRegionsTextChange={setRegionalRegionsText}
        />
        {openViewDialog && (
          <ContestDetailsModal
            open={openViewDialog}
            selectedItem={selectedItem}
            isAdmin={isAdmin}
            onDialogClose={closeDetails}
            onClose={() => setOpenViewDialog(false)}
          />
        )}
        {openDeleteModal && (
          <ConfirmDeleteModal
            contest={true}
            onAccept={confirmDeleteChallenge}
            onReject={() => setOpenDeleteModal(false)}
          />
        )}
      </Box>
    </PageLayout>
  );
}
